import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  Sprout,
  Building2,
  CircleDashed,
  GitFork,
  AlertCircle,
  Plus,
} from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { PageBody } from '@/components/layout/PageBody';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useProject } from '@/contexts/ProjectContext';
import { getAuthSession, getRecommendedWorkflows, listProjects, getProjectHealth } from '@/lib/api';
import { getProjectDisplayName } from '@/lib/repo-label';
import type { ProjectMode, ProjectResponse, ProjectHealthResponse } from '@/lib/api';
import { ConsumeProjectWizard } from '@/components/projects/ConsumeProjectWizard';
import { AddProjectDialog } from '@/components/projects/AddProjectDialog';
import { RepositoryLineagePanel } from '@/components/projects/RepositoryLineagePanel';
import { ProvisionFromProductDialog } from '@/components/projects/ProvisionFromProductDialog';
import { refreshRepositoryLifecycle } from '@/components/projects/repository-lifecycle-ui';
import { getWorkflowDisplayName } from '@/lib/workflow-metadata';

function modeBadge(mode: ProjectMode | null): React.ReactElement {
  if (mode === 'greenfield') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Sprout className="h-3 w-3" /> Greenfield
      </Badge>
    );
  }
  if (mode === 'brownfield') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Building2 className="h-3 w-3" /> Brownfield
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 text-text-tertiary">
      <CircleDashed className="h-3 w-3" /> Not consumed
    </Badge>
  );
}

function HealthBanner({ health }: { health: ProjectHealthResponse }): React.ReactElement | null {
  const blockers = health.issues.filter(i => i.type !== 'unlinked_enrollment');
  const warnings = health.issues.filter(i => i.type === 'unlinked_enrollment');

  if (health.healthy && warnings.length === 0) return null;

  return (
    <Card className={blockers.length > 0 ? 'border-destructive/50' : 'border-warning/50'}>
      <CardContent className="py-3 space-y-1">
        <div className="flex items-center gap-2 text-sm font-medium">
          {blockers.length > 0 ? (
            <AlertCircle className="h-4 w-4 text-destructive" />
          ) : (
            <AlertCircle className="h-4 w-4 text-warning" />
          )}
          Project validation
        </div>
        {blockers.map(issue => (
          <p key={issue.code} className="text-xs text-destructive">
            {issue.detail}
          </p>
        ))}
        {warnings.map(issue => (
          <p key={issue.code} className="text-xs text-text-secondary">
            {issue.detail}
          </p>
        ))}
        <div className="text-xs text-text-tertiary pt-1">
          {health.summary.totalProjects} project{health.summary.totalProjects !== 1 ? 's' : ''} ·{' '}
          {health.summary.linkedEnrollments}/{health.summary.totalEnrollments} enrollments linked
        </div>
      </CardContent>
    </Card>
  );
}

export function ProjectsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const { setSelectedProjectId } = useProject();
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const { data: session } = useQuery({ queryKey: ['authSession'], queryFn: getAuthSession });
  const canProvision = session?.authenticated === true && session.operator?.role === 'admin';
  const {
    data: projects,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: listProjects,
  });

  const { data: health } = useQuery({
    queryKey: ['projects-health'],
    queryFn: getProjectHealth,
    staleTime: 30_000,
  });

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <Header title="Projects" />
      <PageBody contentClassName="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <p className="max-w-2xl text-sm text-text-secondary">
            Create isolated customer repositories from an approved product release, or register
            existing source from GitHub, Gitea, or an enrolled local device; consume it as
            greenfield or brownfield; then open a workbench with the project selected.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={!canProvision}
              onClick={() => {
                setProvisionOpen(true);
              }}
            >
              Create customer repositories
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setAddProjectOpen(true);
              }}
            >
              <Plus className="h-4 w-4" /> Register existing source
            </Button>
          </div>
        </div>
        {!canProvision && (
          <p className="text-sm text-text-secondary">
            An organization administrator can provision customer repositories.
          </p>
        )}
        {isError && (
          <div role="alert">
            Unable to load projects.{' '}
            <Button
              variant="outline"
              onClick={() => {
                void refetch();
              }}
            >
              Retry projects
            </Button>
          </div>
        )}

        {health && <HealthBanner health={health} />}

        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading projects…</div>
        ) : (!projects || projects.length === 0) && !isError ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-text-secondary">
              No projects registered yet. Register a codebase from the chat with{' '}
              <code className="rounded bg-surface-inset px-1 py-0.5 text-xs">
                /register-project
              </code>
              , or register existing source from GitHub, Gitea, or a local-device enrollment.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {projects?.map(project => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}
        <AddProjectDialog
          open={addProjectOpen}
          onOpenChange={setAddProjectOpen}
          onSuccess={codebase => {
            setSelectedProjectId(codebase.id);
            void refreshRepositoryLifecycle(queryClient);
          }}
        />
        {provisionOpen && (
          <ProvisionFromProductDialog
            open={provisionOpen}
            onOpenChange={setProvisionOpen}
            canProvision={canProvision}
          />
        )}
      </PageBody>
    </div>
  );
}

function ProjectCard({ project }: { project: ProjectResponse }): React.ReactElement {
  const navigate = useNavigate();
  const { setSelectedProjectId } = useProject();
  const [wizardOpen, setWizardOpen] = useState(false);

  const codebaseId = project.primary_codebase_id;

  const { data, isLoading } = useQuery({
    queryKey: ['recommended-workflows', codebaseId],
    queryFn: () => (codebaseId ? getRecommendedWorkflows(codebaseId) : Promise.resolve(null)),
    enabled: codebaseId !== null,
  });

  const mode = data?.mode ?? null;
  const rec = data?.recommendation ?? null;
  const nextWorkflow = rec?.recommended[0];

  function goToWorkflows(): void {
    if (codebaseId) setSelectedProjectId(codebaseId);
    void navigate('/workflows');
  }

  return (
    <Card>
      <CardContent className="space-y-3 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <GitFork className="h-3.5 w-3.5 flex-shrink-0 text-text-tertiary" />
              <div className="truncate text-sm font-medium text-text-primary">
                {getProjectDisplayName({ name: project.name })}
              </div>
            </div>
            {project.organization_name && (
              <div className="truncate text-xs text-text-tertiary pl-5">
                {project.organization_name}
              </div>
            )}
            <div className="truncate text-xs text-text-tertiary pl-5 font-mono">
              {project.project_code}
            </div>
          </div>
          {codebaseId && modeBadge(mode)}
        </div>

        {codebaseId === null ? (
          <div className="text-xs text-text-secondary">No primary codebase linked.</div>
        ) : isLoading ? (
          <div className="text-xs text-text-tertiary">Loading status…</div>
        ) : mode === null ? (
          <div className="text-xs text-text-secondary">
            Not yet consumed — classify this project to start lifecycle tracking.
          </div>
        ) : (
          <div className="space-y-1 text-xs text-text-secondary">
            <div>
              Phase:{' '}
              <span className="text-text-primary">
                {rec ? `${String(rec.phase)}. ${rec.phaseName}` : 'not set'}
              </span>
            </div>
            {nextWorkflow && (
              <div>
                Next:{' '}
                <span className="text-text-primary">{getWorkflowDisplayName(nextWorkflow)}</span>
              </div>
            )}
          </div>
        )}

        <RepositoryLineagePanel projectId={project.id} />

        <div className="flex items-center gap-2 pt-1">
          {codebaseId && (
            <Button
              size="sm"
              variant={mode === null ? 'default' : 'outline'}
              onClick={() => {
                setWizardOpen(true);
              }}
            >
              {mode === null ? 'Consume' : 'Re-consume'}
            </Button>
          )}
          {mode !== null && codebaseId && (
            <Button size="sm" variant="ghost" onClick={goToWorkflows}>
              Workflows <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </CardContent>

      {codebaseId && (
        <ConsumeProjectWizard
          codebaseId={codebaseId}
          codebaseName={getProjectDisplayName({ name: project.name })}
          open={wizardOpen}
          onOpenChange={setWizardOpen}
        />
      )}
    </Card>
  );
}
